// Where a DEVICE account goes when it signs into the web app.
//
// Pure and import-free because `main.tsx` imports it statically: anything it
// pulled in would land in the entry chunk, in front of a guest's first paint.
//
// The rule is about the WHOLE permission set, not about holding one capability:
// a manager who is also rostered to operate a lobby kiosk is a person, and
// people get the shell. `every` with a length guard, because `[].every(...)` is
// true for every predicate and an account with no capabilities at all is a
// misconfiguration the shell can say something about.

/** What `main.tsx` does with a signed-in account. */
export type DeviceHome =
  /** A device with a surface in this bundle: navigate there. */
  | { kind: 'redirect'; path: string }
  /** A device whose surface is a native app, not this bundle (docs/16): show
   *  a screen that says so and offers Sign out, never the staff shell. */
  | { kind: 'app-only' }

// `inroom.operate` is the iPad's device account. The browser tablet at
// `/in-room` was removed on 2026-10-28, so there is nowhere in the SPA to send
// it — and the staff shell is the one place it must not land: an empty tab
// strip whose queries all 403 looks exactly like a broken install, and the
// account's credentials belong on a bedside table, not at the front desk.
const REDIRECTS: Array<{ capability: string; path: string }> = [
  { capability: 'kiosk.operate', path: '/kiosk' },
]
const APP_ONLY = ['inroom.operate']

export function deviceHome(capabilities: string[] | undefined): DeviceHome | null {
  const caps = capabilities ?? []
  if (caps.length === 0) return null
  const only = (capability: string) => caps.every((c) => c === capability)
  const redirect = REDIRECTS.find(({ capability }) => only(capability))
  if (redirect) return { kind: 'redirect', path: redirect.path }
  if (APP_ONLY.some(only)) return { kind: 'app-only' }
  return null
}
